import { BicepsFlexed, List, Loader2, Package, Plus, ShoppingCartIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ComboSelectWithLabel } from '../../../components/combo-select-with-label'
import { CustomSelect } from '../../../components/custom-select'
import { Button } from '../../../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { DialogCard } from '../../../modals/modal-post-card'
import { formatToBRL } from '../../../utils/formatCurrency'
import { CartActions } from './actions/cart-actions'
import { FormActionSell } from './actions/form-action-sell'
import { LoadCards } from './actions/load-cards'
import { LoadConsultor } from './actions/load-consultor'
import { PlansTab } from './plans-tab'
import { ShoppingCart } from './shopping-cart'
import { TabProducts } from './tab-products'
import { TabServices } from './tab-services'

interface SellTabProps {
  sellType?: 'VENDA_RAPIDA' | 'VENDA'
  externalMemberSelected: any
}

export default function SellTab({ sellType = 'VENDA', externalMemberSelected }: SellTabProps) {
  const [params] = useSearchParams()
  const memberId = (externalMemberSelected || params.get('tabContentId')) ?? ''
  const [tab, setTab] = useState('plans')
  const { calculateTotal, calculateItemsInCart, cart, setPlanSelected, setProductsSelected, setServiceSelected, updateCart, clearCart } = CartActions()
  const { form, onSubmitSell, isLoading } = FormActionSell(clearCart, calculateItemsInCart, cart, sellType, memberId)

  const { data: cards, loadCards } = LoadCards(sellType)
  const { data: consultor } = LoadConsultor([1, 3, 4, 5, 6, 7, 8, 9, 10])
  const [openCart, setOpenCart] = useState(false)
  const closeCart = () => setOpenCart(false)

  const [dialogCard, setDialogCard] = useState(false)
  const closeDialogCard = () => setDialogCard(false)

  useEffect(() => {
    if (memberId && form.getValues('metodoPagamento') == 'CARTAO_CREDITO') loadCards(memberId)
  }, [form.watch('metodoPagamento')])

  useEffect(() => {
    if (externalMemberSelected) {
      clearCart()
      form.reset()
    }
  }, [externalMemberSelected])

  return (
    <div className="space-y-4 w-full mx-auto">
      {openCart && <ShoppingCart closeShoppingCart={closeCart} cart={cart} updateCart={updateCart} calculateTotal={calculateTotal} />}
      {dialogCard && (
        <DialogCard type={sellType == 'VENDA' ? 'ALUNO' : 'CLIENTE'} idUsuario={Number(memberId)} closeDialog={closeDialogCard} updateList={() => loadCards(memberId)} isOpen={dialogCard} />
      )}

      <div className="w-full flex gap-2 ">
        <Tabs defaultValue="plans" onValueChange={setTab} className="flex-1">
          <TabsList className="flex gap-x-4">
            <TabsTrigger value="plans" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted flex gap-2 items-center justify-center flex-1 px-4 py-2">
              <List className="text-sm" />
              Planos
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted flex gap-2 items-center justify-center flex-1 px-4 py-2">
              <Package className="text-sm" />
              Produtos
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted flex gap-2 items-center justify-center flex-1 px-4 py-2">
              <BicepsFlexed className="text-sm" />
              Serviços
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center justify-center w-24 rounded-md shadow-md cursor-pointer mt-[-2px] relative" onClick={() => setOpenCart(true)}>
          <div className="absolute top-[-4px] right-[-10px] bg-acelerafit-green-400 text-muted rounded-full shadow-md p-1 text-xs w-8 h-8 items-center flex justify-center font-bold">
            {calculateItemsInCart()}
          </div>
          <ShoppingCartIcon />
        </div>
      </div>

      <div className="">
        {tab == 'plans' && <PlansTab setPlanSelected={setPlanSelected} defaultPlan={cart.planos[0]?.id} valueWatcher={externalMemberSelected} />}

        {tab == 'products' && <TabProducts setProductsSelected={setProductsSelected} valueWatcher={externalMemberSelected} />}

        {tab == 'services' && <TabServices setServiceSelected={setServiceSelected} defaultService={cart.servicos[0]?.id} valueWatcher={externalMemberSelected} />}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmitSell)} className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name="consultor.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Consultor</FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={consultor || []}
                    onChange={(e) => {
                      const id = e?.value
                      form.setValue('consultor.id', id)
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.consultor?.id?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="metodoPagamento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Método de pagamento:</FormLabel>
                <CustomSelect onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="BOLETO">Boleto - PIX</SelectItem>
                    <SelectItem value="CARTAO_CREDITO">Cartão de Credito</SelectItem>
                    <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                  </SelectContent>
                </CustomSelect>
                <FormMessage className="text-xs">{form.formState.errors.metodoPagamento?.message}</FormMessage>
              </FormItem>
            )}
          />
          {form.watch('metodoPagamento') == 'CARTAO_CREDITO' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="grid grid-cols-[1fr_50px] gap-2">
                <FormField
                  control={form.control}
                  name="detalhesCartao.id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cartão</FormLabel>
                      <FormControl>
                        <ComboSelectWithLabel
                          defaultValue={field.value}
                          data={cards || []}
                          field={field.ref}
                          onChange={(e) => {
                            console.debug({ e })
                            form.setValue('detalhesCartao.id', e?.id.toString())
                            form.setValue('detalhesCartao.tokenCartao', e?.token)
                          }}
                        />
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.detalhesCartao?.id?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <Button type="button" className="w-12 h-10 bg-acelerafit-green-400 text-muted font-bold hover:bg-acelerafit-green-50 mt-8" onClick={() => setDialogCard(true)}>
                  <Plus />
                </Button>
              </div>

              <FormField
                control={form.control}
                name="detalhesCartao.quantidadeParcelas"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parcelamento:</FormLabel>
                    <CustomSelect onValueChange={field.onChange} defaultValue={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">1x</SelectItem>
                        <SelectItem value="2">2x</SelectItem>
                        <SelectItem value="3">3x</SelectItem>
                        <SelectItem value="4">4x</SelectItem>
                        <SelectItem value="5">5x</SelectItem>
                        <SelectItem value="6">6x</SelectItem>
                        <SelectItem value="7">7x</SelectItem>
                        <SelectItem value="8">8x</SelectItem>
                        <SelectItem value="9">9x</SelectItem>
                        <SelectItem value="10">10x</SelectItem>
                        <SelectItem value="11">11x</SelectItem>
                        <SelectItem value="12">12x</SelectItem>
                      </SelectContent>
                    </CustomSelect>
                    <FormMessage className="text-xs">{form.formState.errors.metodoPagamento?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          )}
          <div className="flex justify-between items-center mt-2">
            <h3>
              <strong>Total:</strong> {formatToBRL(calculateTotal(cart))}
            </h3>
            <Button type="submit" className="bg-acelerafit-green-400 text-muted hover:bg-acelerafit-green-200" disabled={!calculateItemsInCart() || isLoading}>
              {isLoading ? <Loader2 className="mr-2 animate-spin" /> : ' Finalizar venda'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
